<template>
  <div class="task-list__wrapper">
    <button
      class="task-list__left text-left"
      @click="move('left')"
      v-show="scrollPercentage !== 0"
    >
      <svg-icon :icon="`chevron-left`" class="mr-1" />
    </button>
    <div
      class="task-list"
      @scroll.passive="onScroll"
      ref="taskList"
      v-dragscroll
      @dragscrollend="onDrag"
      @dragscrollstart="onDrag"
    >
      <list-tasks :id="id" :list="sortedTasks" class="d-flex">
        <li slot="before">
          <a
            @mouseup="mouseup($event, `/campaigns/${id}`)"
            :class="{ 'is-active': !$route.params.name }"
            class="task-list__item"
          >
            <svg-icon icon="info-round" class="icon-2x mr-2 text-primary" />
            <span class="task-list__item__name">{{ $t('info') }}</span>
          </a>
        </li>
        <li slot="task" slot-scope="{ task }">
          <a
            @mouseup="
              mouseup($event, `/campaigns/${id}/${task.title.toLowerCase()}`)
            "
            :class="{
              'is-active':
                $route.params.name &&
                $route.params.name.toLowerCase() === task.title.toLowerCase()
            }"
            class="task-list__item"
          >
            <template v-if="$can('join', 'Task')">
              <svg-icon
                v-if="task.joined"
                icon="check"
                class="icon-sm text-success mr-1"
              />
              <svg-icon v-else icon="cross" class="icon-sm text-danger mr-1" />
            </template>
            <svg-icon
              :icon="`${task.title.replace(/\s/g, '').toLowerCase()}-round`"
              class="icon-2x mr-1"
            />
            <span class="task-list__item__name">
              {{ task.title | capitalize }}
            </span>
            <span v-if="task.percent" class="badge badge-success ml-3">
              {{ task.percent }} %
            </span>
          </a>
        </li>
      </list-tasks>
    </div>
    <button
      class="task-list__right text-right"
      @click="move('right')"
      v-show="scrollPercentage <= 100"
    >
      <svg-icon :icon="`chevron-right`" class="mr-1" />
    </button>
  </div>
</template>

<script>
import ListTasks from '@/components/BountyCampaign/ListTasks.vue';
import { dragscroll } from 'vue-dragscroll';
import cloneDeep from 'clone';

function currentScrollPercentage(el) {
  return (el.scrollLeft / (el.scrollWidth - el.clientWidth)) * 100;
}

export default {
  props: {
    id: {
      type: String,
      required: true
    },
    tasks: {
      type: Array,
      required: true
    }
  },

  directives: {
    dragscroll
  },

  data() {
    return {
      dragging: false,
      dragTolerance: null,
      scrollPercentage: 0
    };
  },

  components: {
    ListTasks
  },

  computed: {
    sortedTasks() {
      const newArr = cloneDeep(this.tasks);

      return newArr.sort((x, y) => y.joined - x.joined);
    }
  },

  methods: {
    onDrag(e) {
      e.stopPropagation();
      // таймаут нужен что б при случайный движениях курсора не блокировался клик по ссылке
      if (e.type === 'dragscrollstart') {
        this.dragTolerance = setTimeout(() => {
          this.dragging = true;
        }, 100);
      }
      if (e.type === 'dragscrollend') {
        this.dragging = false;
        clearTimeout(this.dragTolerance);
      }
    },
    onScroll(e) {
      this.scrollPercentage = currentScrollPercentage(e.target);
    },
    mouseup(e, url) {
      if (this.dragging) return;
      this.$router.push(url);
    },
    move(direction) {
      this.smoothSideScroll(this.$refs.taskList, direction, 5, 150, 5);
    },
    smoothSideScroll(element, direction, speed, distance, step) {
      let scrollAmount = 0;
      let slideTimer = setInterval(function() {
        if (direction === 'left') {
          element.scrollLeft -= step;
        } else {
          element.scrollLeft += step;
        }
        scrollAmount += step;
        if (scrollAmount >= distance) {
          window.clearInterval(slideTimer);
        }
      }, speed);
    }
  }
};
</script>

<style lang="scss" scoped>
.task-list {
  overflow: auto;
  padding-bottom: 40px;
  -webkit-overflow-scrolling: touch;

  &__wrapper {
    height: 55px;
    overflow: hidden;
    box-shadow: inset 0 -1px 0px 0px $border-color;
  }

  &__left,
  &__right {
    position: absolute;
    top: 0;
    width: 50px;
    height: 100%;
    border: none;
    border-bottom: 1px solid $border-color;
    z-index: 2;
    cursor: pointer;
    outline: none;
    color: $light;

    &:hover {
      color: $primary-hover;
    }
  }

  &__right {
    right: 0;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(255, 255, 255, 1) 100%
    );
  }

  &__left {
    left: 0;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0.8) 50%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  &__item {
    white-space: nowrap;
    text-align: center;
    padding-right: 30px;
    padding-left: 30px;
    text-decoration: none;
    color: $body-color;
    position: relative;
    display: inline-block;
    margin-right: 1px;
    cursor: pointer;

    &:before {
      content: ' ';
      display: block;
      width: 100%;
      height: 3px;
      background: $primary;
      position: absolute;
      bottom: -0px;
      width: 80%;
      left: 10%;
      border-top-right-radius: 3px;
      border-top-left-radius: 3px;
      opacity: 0;
      z-index: 2;
      @extend %base-transition;
    }

    &:hover,
    &.is-active {
      &:before {
        opacity: 1;
      }
    }

    &:after {
      content: '';
      display: block;
      width: 0;
      height: 30px;
      right: 0;
      border-right: 1px solid $border-color;
      position: absolute;
      right: -1px;
      top: 50%;
      transform: translateY(-50%);
    }

    &:last-child {
      &:after {
        display: none;
      }
    }

    &__name {
      line-height: 55px;
    }

    & > i,
    & > span {
      display: inline-block;
      vertical-align: middle;
    }
  }
}
</style>
